import apiEndPoints from "./apiEndPoints";
import { VISITOR_MOBILE_OTP_ENABLED } from "./constants";
import { apiCall } from "./httpClient";
import { mobileNumberValid } from "./utils";

export function isVisitorMobileOtpEnabled() {
  return VISITOR_MOBILE_OTP_ENABLED;
}

export function isPropertyGreMobileOtpEnabled(property) {
  if (!property) {
    return false;
  }
  return Boolean(property.gre_mobile_otp_enabled);
}

export function resolveGreMobileOtpEnabled(propertyId, propertyPools = []) {
  if (!propertyId) {
    return false;
  }
  for (const pool of propertyPools) {
    const match = pool?.find(
      (item) =>
        String(item?.property_id ?? item?._id ?? "") === String(propertyId),
    );
    if (match) {
      return isPropertyGreMobileOtpEnabled(match);
    }
  }
  return false;
}

/** When a GRE has exactly one allocated property, use it before manual dropdown selection. */
export function resolveGreDefaultProperty(propertyPools = []) {
  for (const pool of propertyPools) {
    if (!Array.isArray(pool) || pool.length !== 1) {
      continue;
    }
    const item = pool[0];
    const propertyId = String(item?.property_id ?? item?._id ?? "").trim();
    if (!propertyId) {
      continue;
    }
    return {
      property_id: propertyId,
      property_title: item?.property_title || "",
      gre_mobile_otp_enabled: Boolean(item?.gre_mobile_otp_enabled),
    };
  }
  return null;
}

export function isGreVisitorOtpRequired(isGreUser, isPropertyOtpEnabled) {
  return Boolean(
    isGreUser && isVisitorMobileOtpEnabled() && isPropertyOtpEnabled,
  );
}

export function isGreAddVisitorOtpRequired(
  isGreUser,
  isEditMode,
  isPropertyOtpEnabled,
) {
  return isGreVisitorOtpRequired(isGreUser, isPropertyOtpEnabled);
}

export const initialVisitorMobileOtpState = {
  mobileVerified: false,
  verifiedMobile: "",
  otpSent: false,
  otp: "",
  sendingOtp: false,
  verifyingOtp: false,
  otpSendError: "",
};

export function isGreMobileCurrentlyVerified(mobile, otpState) {
  const digits = String(mobile || "").trim();
  return Boolean(
    otpState?.verifiedMobile &&
      digits &&
      digits === otpState.verifiedMobile &&
      otpState.mobileVerified,
  );
}

/** True when OTP state or persisted form flag says this mobile is verified. */
export function isGreMobileVerificationSatisfied(
  mobile,
  otpState,
  visitConfirmationStatus,
) {
  const digits = String(mobile || "").trim();
  return Boolean(
    isGreMobileCurrentlyVerified(digits, otpState) ||
      (isVisitorMobileValid(digits) && Number(visitConfirmationStatus) === 1),
  );
}

export function buildVerifiedGreMobileOtpState(mobile) {
  const digits = String(mobile || "").trim();
  return {
    mobileVerified: true,
    verifiedMobile: digits,
    otpSent: false,
    otp: "",
    sendingOtp: false,
    verifyingOtp: false,
    otpSendError: "",
  };
}

export function resolveGreMobileVerificationSnapshot(
  mobile,
  otpState,
  _visitConfirmationStatus,
) {
  // Only a real OTP session counts — never upgrade synthetic
  // visit_confirmation_status=1 into otpState.mobileVerified.
  const isVerified = isGreMobileCurrentlyVerified(mobile, otpState);

  return {
    isVerified,
    visitConfirmationStatus: isVerified ? 1 : "",
    otpState: isVerified ? buildVerifiedGreMobileOtpState(mobile) : null,
  };
}

/** Keep visit_confirmation_status when non-mobile fields change (e.g. property, lead source). */
export function greVisitConfirmationStatusForMobile(
  mobile,
  otpState,
  visitConfirmationStatus,
) {
  return isGreMobileVerificationSatisfied(mobile, otpState, visitConfirmationStatus)
    ? 1
    : "";
}

/**
 * When the GRE picks / switches property, only keep verification from a real
 * OTP session. Do not trust synthetic visit_confirmation_status=1.
 */
export function resolveGrePropertyChangeOtpState(mobile, otpState) {
  const currentMobile = String(mobile || "").trim();
  const wasMobileVerified = isGreMobileCurrentlyVerified(
    currentMobile,
    otpState,
  );

  if (wasMobileVerified) {
    return {
      otpState: buildVerifiedGreMobileOtpState(currentMobile),
      visitConfirmationStatus: 1,
    };
  }

  return {
    otpState: { ...initialVisitorMobileOtpState },
    visitConfirmationStatus: "",
  };
}

export function shouldShowGreSendOtp(mobile, otpState) {
  const digits = String(mobile || "").trim();
  if (!isVisitorMobileValid(digits)) {
    return false;
  }
  if (otpState?.otpSent) {
    return false;
  }
  if (isGreMobileCurrentlyVerified(digits, otpState)) {
    return false;
  }
  return digits !== String(otpState?.verifiedMobile || "").trim();
}

export function isVisitorMobileValid(mobile, countryCode = "+91") {
  if (!mobile) {
    return false;
  }
  if (countryCode === "+91") {
    return mobileNumberValid(mobile);
  }
  return String(mobile).length >= 10;
}

function visitorOtpRequestPayload(payload) {
  return {
    ...payload,
    suppressGlobalLoader: true,
  };
}

export async function sendVisitorMobileOtp(
  mobile,
  countryCode = "+91",
  propertyTitle = "",
  propertyId = "",
) {
  return apiCall(
    "post",
    apiEndPoints.SEND_VISITOR_MOBILE_OTP,
    visitorOtpRequestPayload({
      mobile,
      country_code: countryCode,
      property_title: propertyTitle || "",
      property_id: propertyId || "",
    }),
  );
}

/** Prefer short dev OTP toast so the code is visible on mobile. */
export function resolveVisitorOtpSentMessage(
  responseData,
  fallback = "OTP sent successfully",
) {
  const devOtp = responseData?.dev_otp;
  if (devOtp !== undefined && devOtp !== null && String(devOtp).trim() !== "") {
    return `Dev environment configured. Your OTP is ${String(devOtp).trim()}`;
  }
  return responseData?.message || fallback;
}

export function isVisitorOtpSendSuccess(responseData) {
  if (!responseData || Number(responseData.status) !== 200) {
    return false;
  }
  const hasDevOtp =
    responseData.dev_otp !== undefined &&
    responseData.dev_otp !== null &&
    String(responseData.dev_otp).trim() !== "";
  if (responseData.sms_sent === false && !hasDevOtp) {
    return false;
  }
  return true;
}

export function resolveVisitorOtpFailureMessage(
  responseData,
  fallback = "OTP SMS could not be sent. Please contact your administrator.",
) {
  const message = String(responseData?.message || "").trim();
  return message || fallback;
}

export async function verifyVisitorMobileOtp(
  mobile,
  countryCode = "+91",
  otp,
  propertyId = "",
) {
  return apiCall(
    "post",
    apiEndPoints.VERIFY_VISITOR_MOBILE_OTP,
    visitorOtpRequestPayload({
      mobile,
      country_code: countryCode,
      otp,
      property_id: propertyId || "",
    }),
  );
}

export async function resendVisitorMobileOtp(
  mobile,
  countryCode = "+91",
  propertyTitle = "",
  propertyId = "",
) {
  return apiCall(
    "post",
    apiEndPoints.RESEND_VISITOR_MOBILE_OTP,
    visitorOtpRequestPayload({
      mobile,
      country_code: countryCode,
      property_title: propertyTitle || "",
      property_id: propertyId || "",
    }),
  );
}
